<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { format, fromUnixTime, isSameYear } from 'date-fns';
import { ptBR, enUS, es, fr, de, it, ja, ko, ru, zhCN } from 'date-fns/locale';

import MessageStatus from './MessageStatus.vue';
import Icon from 'next/icon/Icon.vue';
import { useInbox } from 'dashboard/composables/useInbox';
import { useMessageContext } from './provider.js';

import { MESSAGE_STATUS, MESSAGE_TYPES } from './constants';

const {
  isAFacebookInbox,
  isALineChannel,
  isAPIInbox,
  isASmsInbox,
  isATelegramChannel,
  isATwilioChannel,
  isAWebWidgetInbox,
  isAWhatsAppChannel,
  isAnEmailChannel,
  isAnInstagramChannel,
  isATiktokChannel,
  isAQuepasaChannel,
} = useInbox();

const { t, locale: i18nLocale } = useI18n();

const {
  status,
  isPrivate,
  createdAt,
  receivedAt,
  actualCreatedAt,
  sourceId,
  messageType,
  contentAttributes,
} = useMessageContext();

// Mapeamento de locales do i18n para date-fns
const localeMap = {
  pt_BR: ptBR,
  'pt-BR': ptBR,
  pt: ptBR,
  en: enUS,
  es: es,
  fr: fr,
  de: de,
  it: it,
  ja: ja,
  ko: ko,
  ru: ru,
  zh_CN: zhCN,
  'zh-CN': zhCN,
};

// Helper para formatar timestamp com locale do usuário
const formatTimestamp = time => {
  const messageTime = fromUnixTime(time);
  const now = new Date();
  const userLocale = localeMap[i18nLocale.value] || enUS;

  // Para pt-BR usa formato 24h, outros mantêm 12h
  const is24Hour = i18nLocale.value.startsWith('pt');
  const timeFormat = is24Hour ? 'HH:mm:ss' : 'h:mm:ss a';

  if (!isSameYear(messageTime, now)) {
    return format(messageTime, `d 'de' MMM 'de' yyyy, ${timeFormat}`, {
      locale: userLocale,
    });
  }

  return format(messageTime, `d 'de' MMM, ${timeFormat}`, {
    locale: userLocale,
  });
};

const readableTime = computed(() => formatTimestamp(createdAt.value));

const readableReceivedAt = computed(() =>
  receivedAt.value ? formatTimestamp(receivedAt.value) : null
);

const readableActualCreatedAt = computed(() =>
  actualCreatedAt.value ? formatTimestamp(Number(actualCreatedAt.value)) : 'N/A'
);

const showDetailedTimestamps = computed(() => {
  const received = Number(receivedAt.value);
  return received > 0 && received !== Number(createdAt.value);
});

const shouldShowReceivedAt = computed(() => {
  return (
    readableReceivedAt.value && readableReceivedAt.value !== readableTime.value
  );
});

// Edited indicator (WhatsApp edits routed by channel-quepasa set
// content_attributes.edited + quepasa_channel_edited_at ISO timestamp).
const editedAtLabel = computed(() => {
  const attrs = contentAttributes.value || {};
  if (!attrs.edited) return null;
  // context keys arrive camelized in the new UI; keep the snake_case
  // fallback for raw payload paths.
  const iso = attrs.quepasaChannelEditedAt || attrs.quepasa_channel_edited_at;
  if (!iso) return '';
  const ts = Math.floor(new Date(iso).getTime() / 1000);
  return Number.isFinite(ts) && ts > 0 ? formatTimestamp(ts) : '';
});

const showStatusIndicator = computed(() => {
  if (isPrivate.value) return false;
  // Don't show status for failed messages, we already show error message
  if (status.value === MESSAGE_STATUS.FAILED) return false;
  // Don't show status for deleted messages
  if (contentAttributes.value?.deleted) return false;

  if (messageType.value === MESSAGE_TYPES.OUTGOING) return true;
  if (messageType.value === MESSAGE_TYPES.TEMPLATE) return true;

  return false;
});

const isSent = computed(() => {
  if (!showStatusIndicator.value) return false;

  // Messages will be marked as sent for the Email channel if they have a source ID.
  if (isAnEmailChannel.value) return !!sourceId.value;

  if (
    isAWhatsAppChannel.value ||
    isAQuepasaChannel.value ||
    isATwilioChannel.value ||
    isAFacebookInbox.value ||
    isASmsInbox.value ||
    isATelegramChannel.value ||
    isAnInstagramChannel.value ||
    isATiktokChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.SENT;
  }

  // All messages will be mark as sent for the Line channel, as there is no source ID.
  if (isALineChannel.value) return true;

  return false;
});

const isDelivered = computed(() => {
  if (!showStatusIndicator.value) return false;

  if (
    isAWhatsAppChannel.value ||
    isAQuepasaChannel.value ||
    isATwilioChannel.value ||
    isASmsInbox.value ||
    isAFacebookInbox.value ||
    isATiktokChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.DELIVERED;
  }
  // All messages marked as delivered for the web widget inbox and API inbox once they are sent.
  if (isAWebWidgetInbox.value || isAPIInbox.value) {
    return status.value === MESSAGE_STATUS.SENT;
  }
  if (isALineChannel.value) {
    return status.value === MESSAGE_STATUS.DELIVERED;
  }

  return false;
});

const isRead = computed(() => {
  if (!showStatusIndicator.value) return false;

  if (
    isAWhatsAppChannel.value ||
    isAQuepasaChannel.value ||
    isATwilioChannel.value ||
    isAFacebookInbox.value ||
    isAnInstagramChannel.value ||
    isATiktokChannel.value
  ) {
    return sourceId.value && status.value === MESSAGE_STATUS.READ;
  }

  if (isAWebWidgetInbox.value || isAPIInbox.value) {
    return status.value === MESSAGE_STATUS.READ;
  }

  return false;
});

const statusToShow = computed(() => {
  if (isRead.value) return MESSAGE_STATUS.READ;
  if (isDelivered.value) return MESSAGE_STATUS.DELIVERED;
  if (isSent.value) return MESSAGE_STATUS.SENT;

  return MESSAGE_STATUS.PROGRESS;
});
</script>

<template>
  <div class="text-xs flex items-center gap-1.5">
    <div v-if="showDetailedTimestamps" class="block">
      <div class="flex flex-col gap-0.5 mb-1">
        <time v-if="shouldShowReceivedAt" class="block text-slate-500">
          {{ $t('CONVERSATION.MESSAGES.RECEIVED_AT') }}:
          {{ readableReceivedAt }}
        </time>
        <time class="block text-slate-400">
          {{ $t('CONVERSATION.MESSAGES.CREATED_AT') }}:
          {{ readableActualCreatedAt }}
        </time>
      </div>
    </div>
    <div class="block">
      <time v-if="shouldShowReceivedAt" class="block">
        {{ $t('CONVERSATION.MESSAGES.RECEIVED_AT') }}: {{ readableReceivedAt }}
      </time>
      <time class="block">
        {{ $t('CONVERSATION.MESSAGES.CREATED_AT') }}: {{ readableTime }}
      </time>
      <time v-if="editedAtLabel !== null" class="block italic">
        {{ $t('CONVERSATION.MESSAGES.EDITED_AT')
        }}{{ editedAtLabel ? `: ${editedAtLabel}` : '' }}
      </time>
    </div>
    <Icon v-if="isPrivate" icon="i-lucide-lock-keyhole" class="size-3" />
    <MessageStatus v-if="showStatusIndicator" :status="statusToShow" />
  </div>
</template>
